import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import {
  Mail,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Copy,
  Eye,
  Sparkles,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { projectId } from '../utils/supabase/info';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: 'client_invitation' | 'user_invitation' | 'custom';
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EmailTemplateManagerProps {
  accessToken: string;
  firmId: string;
}

export function EmailTemplateManager({ accessToken, firmId }: EmailTemplateManagerProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateType, setTemplateType] = useState<'client_invitation' | 'user_invitation' | 'custom'>('client_invitation');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        setError('Failed to load email templates');
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateSubject('');
    setTemplateBody('');
    setTemplateType('client_invitation');
    setIsDefault(false);
    setShowEditor(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateSubject(template.subject);
    setTemplateBody(template.body);
    setTemplateType(template.type);
    setIsDefault(template.isDefault);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!templateName.trim() || !templateSubject.trim() || !templateBody.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const url = editingTemplate
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-templates/${editingTemplate.id}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-templates`;

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: templateName,
          subject: templateSubject,
          body: templateBody,
          type: templateType,
          isDefault,
        }),
      });

      if (response.ok) {
        setSuccess(editingTemplate ? 'Template updated successfully' : 'Template created successfully');
        setShowEditor(false);
        await loadTemplates();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save template');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setSuccess('Template deleted successfully');
        await loadTemplates();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete template');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  const handlePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const insertVariable = (variable: string) => {
    setTemplateBody(prev => prev + `{${variable}}`);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'client_invitation': return 'Client Invitation';
      case 'user_invitation': return 'User Invitation';
      case 'custom': return 'Custom';
      default: return type;
    }
  };

  const availableVariables = [
    { key: 'client_name', label: 'Client Name', description: 'The name of the invited user' },
    { key: 'company_name', label: 'Company Name', description: 'The client company name' },
    { key: 'login_url', label: 'Login URL', description: 'The login/signup link' },
    { key: 'firm_name', label: 'Firm Name', description: 'Your consulting firm name' },
    { key: 'consultant_name', label: 'Consultant Name', description: 'The inviting consultant\'s name' },
    { key: 'project_name', label: 'Project Name', description: 'The project/engagement name (if applicable)' },
  ];

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground">Loading email templates...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {success}
          </AlertDescription>
        </Alert>
      )}

      <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
        <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <strong>Email Templates (ETs):</strong> Create professional, branded email templates for client invitations, user invitations, and custom communications. Use variables like {'{client_name}'}, {'{company_name}'}, and {'{login_url}'} to personalize your messages.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>
                Manage email templates for client communications
              </CardDescription>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No email templates yet</p>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Template
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{template.name}</h3>
                        {template.isDefault && (
                          <Badge variant="default" className="text-xs">
                            Default
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {getTypeLabel(template.type)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Subject: {template.subject}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {template.body.substring(0, 150)}...
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(template)}
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
            </DialogTitle>
            <DialogDescription>
              Use variables like {'{client_name}'}, {'{company_name}'}, and {'{login_url}'} in your template
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Welcome Email, Project Kickoff"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-type">Template Type</Label>
              <Select value={templateType} onValueChange={(value: any) => setTemplateType(value)}>
                <SelectTrigger id="template-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_invitation">Client Invitation</SelectItem>
                  <SelectItem value="user_invitation">User Invitation</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is-default"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="is-default">Set as default template for this type</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-subject">Email Subject *</Label>
              <Input
                id="template-subject"
                value={templateSubject}
                onChange={(e) => setTemplateSubject(e.target.value)}
                placeholder="Welcome to {company_name}"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="template-body">Email Body *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Insert variable:</span>
                  {availableVariables.slice(0, 3).map((variable) => (
                    <Button
                      key={variable.key}
                      variant="outline"
                      size="sm"
                      onClick={() => insertVariable(variable.key)}
                      className="text-xs h-7 px-2"
                    >
                      {variable.label}
                    </Button>
                  ))}
                </div>
              </div>
              <Textarea
                id="template-body"
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                placeholder="Hi {client_name},&#10;&#10;Welcome to {company_name}. Your account has been set up and is ready for use.&#10;&#10;Click here to get started: {login_url}"
                rows={12}
                className="font-mono text-sm"
              />
            </div>

            <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
              <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                <div className="space-y-2">
                  <p className="font-semibold">Available Variables:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {availableVariables.map((variable) => (
                      <div key={variable.key}>
                        <code className="bg-white dark:bg-gray-800 px-1 py-0.5 rounded">
                          {'{' + variable.key + '}'}
                        </code>
                        <span className="text-muted-foreground ml-1">- {variable.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingTemplate ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              Variables will be replaced with actual data when sent
            </DialogDescription>
          </DialogHeader>

          {previewTemplate && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs text-muted-foreground">SUBJECT</Label>
                <p className="font-semibold mt-1">{previewTemplate.subject}</p>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">BODY</Label>
                <div className="mt-2 p-4 bg-muted/50 rounded-lg whitespace-pre-wrap">
                  {previewTemplate.body}
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">SAMPLE (with variables replaced)</Label>
                <div className="mt-2 p-4 bg-white dark:bg-gray-900 border rounded-lg">
                  <p className="font-semibold mb-4">
                    {previewTemplate.subject
                      .replace('{client_name}', 'John Doe')
                      .replace('{company_name}', 'Acme Corp')
                      .replace('{firm_name}', 'Your Consulting Firm')}
                  </p>
                  <div className="whitespace-pre-wrap text-sm">
                    {previewTemplate.body
                      .replace(/{client_name}/g, 'John Doe')
                      .replace(/{company_name}/g, 'Acme Corp')
                      .replace(/{firm_name}/g, 'Your Consulting Firm')
                      .replace(/{consultant_name}/g, 'Jane Smith')
                      .replace(/{login_url}/g, 'https://acmecorp.pathkit.pro/login')
                      .replace(/{project_name}/g, 'Q4 Implementation')}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}